import React, { useState, useMemo, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import {
  Heart, ArrowLeft, ArrowRight, Share2, Loader2, X, Sparkles,
  RotateCcw, Mail, CheckCircle2, Brain,
} from "lucide-react";
import { api, vibrate } from "@/lib/api";
import { APP_URL, APP_DOMAIN } from "@/config";
import { useAuth } from "@/context/AuthContext";
import SocialProof from "@/components/SocialProof";

const STYLES = {
  secure: {
    emoji: "🌿", title: "Sécure", color: "#10b981", accent: "#6ee7b7",
    g: ["#04160f", "#0b3a26", "#124b30"],
    short: "Solide et serein·e en amour",
    desc: "Confiance en toi et en l'autre. Tu sais donner de l'amour sans t'oublier, poser tes limites et communiquer tes besoins calmement.",
    couple: "Tu crées des relations stables et rassurantes. Tu apaises les partenaires plus anxieux et sécurises les évitants.",
    tips: ["Continue à communiquer clairement", "Reste ton propre point d'ancrage", "Choisis des partenaires qui respectent ton équilibre"],
  },
  anxious: {
    emoji: "😰", title: "Anxieux", color: "#f472b6", accent: "#f9a8d4",
    g: ["#2a0a1e", "#5b0f2e", "#7a1533"],
    short: "Tu aimes fort, très fort",
    desc: "Tu t'investis à fond et tu as besoin d'être rassuré·e. La peur de l'abandon peut te pousser à sur-analyser les messages et à chercher des preuves d'amour.",
    couple: "Tu es passionné·e et attentionné·e, mais le manque de réponse peut vite t'angoisser. Un partenaire sécure te fait un bien fou.",
    tips: ["Apprends à t'auto-rassurer avant de relancer", "Exprime tes besoins sans reproche", "Fuis les partenaires qui soufflent le chaud et le froid"],
  },
  avoidant: {
    emoji: "🥶", title: "Évitant", color: "#38bdf8", accent: "#7dd3fc",
    g: ["#04121f", "#0b2a45", "#123b5e"],
    short: "Ton indépendance avant tout",
    desc: "Tu tiens à ta liberté et ta bulle. Quand une relation devient trop proche, tu ressens le besoin de prendre de la distance pour respirer.",
    couple: "Tu es autonome et rassurant·e en surface, mais l'intimité profonde peut t'angoisser. Le risque : fuir juste quand ça devient sérieux.",
    tips: ["Ose nommer ce que tu ressens", "Rappelle-toi que proximité ≠ perte de liberté", "Préviens quand tu as besoin d'espace au lieu de disparaître"],
  },
  disorganized: {
    emoji: "🎢", title: "Désorganisé", color: "#f59e0b", accent: "#fcd34d",
    g: ["#241004", "#4a2205", "#6b3410"],
    short: "Le grand écart émotionnel",
    desc: "Tu oscilles entre un immense désir de fusion et la peur d'être blessé·e. Résultat : des relations en montagnes russes, chaud puis froid.",
    couple: "Tu vis l'amour intensément mais de façon instable. Comprendre tes déclencheurs t'aidera à sortir du cycle attirance/rejet.",
    tips: ["Repère tes déclencheurs de panique", "Vise des partenaires stables et prévisibles", "Un accompagnement peut vraiment t'apaiser"],
  },
};

const QUESTIONS = [
  { q: "Quand un message reste sans réponse pendant des heures, la réaction est plutôt…", o: [
    { t: "Rester zen, la réponse viendra", s: "secure" },
    { t: "Angoisser, imaginer le pire, relancer", s: "anxious" },
    { t: "S'en ficher, passer à autre chose", s: "avoidant" },
    { t: "Vouloir écrire… puis se braquer d'un coup", s: "disorganized" },
  ]},
  { q: "Sur l'engagement dans une relation, la tendance est de…", o: [
    { t: "S'investir sereinement quand la confiance est là", s: "secure" },
    { t: "Vouloir tout, vite et fort", s: "anxious" },
    { t: "Garder une porte de sortie", s: "avoidant" },
    { t: "Désirer la fusion mais paniquer quand c'est réel", s: "disorganized" },
  ]},
  { q: "Face à un conflit ou une dispute…", o: [
    { t: "En parler calmement pour trouver une solution", s: "secure" },
    { t: "Avoir peur que ça casse tout, réparer vite", s: "anxious" },
    { t: "Se fermer et prendre de la distance", s: "avoidant" },
    { t: "Passer de l'explosion au silence total", s: "disorganized" },
  ]},
  { q: "Le besoin de réassurance (« tu m'aimes ? », « tout va bien ? ») est…", o: [
    { t: "Rare, la confiance suffit", s: "secure" },
    { t: "Fréquent, ça rassure beaucoup", s: "anxious" },
    { t: "Presque nul, demander met mal à l'aise", s: "avoidant" },
    { t: "Très variable selon les jours", s: "disorganized" },
  ]},
  { q: "Quand la relation devient sérieuse et très proche…", o: [
    { t: "C'est agréable, on avance ensemble", s: "secure" },
    { t: "Enfin ! on ne veut plus se quitter", s: "anxious" },
    { t: "Ça devient étouffant, besoin d'air", s: "avoidant" },
    { t: "Envie d'y aller… et de fuir en même temps", s: "disorganized" },
  ]},
  { q: "L'indépendance et le temps pour soi, c'est…", o: [
    { t: "Important pour les deux, sans culpabilité", s: "secure" },
    { t: "Difficile, on préfère tout partager", s: "anxious" },
    { t: "Vital, on protège sa bulle", s: "avoidant" },
    { t: "Une source de tiraillement permanent", s: "disorganized" },
  ]},
  { q: "Exprimer ses émotions et ses besoins…", o: [
    { t: "Plutôt facile et direct", s: "secure" },
    { t: "On en dit beaucoup, parfois trop", s: "anxious" },
    { t: "Compliqué, on garde pour soi", s: "avoidant" },
    { t: "Ça sort par vagues, souvent maladroitement", s: "disorganized" },
  ]},
  { q: "La peur numéro 1 en amour, c'est…", o: [
    { t: "Aucune peur qui domine, plutôt confiant·e", s: "secure" },
    { t: "Être abandonné·e ou pas assez aimé·e", s: "anxious" },
    { t: "Perdre sa liberté, être envahi·e", s: "avoidant" },
    { t: "Être blessé·e par la personne dont on a besoin", s: "disorganized" },
  ]},
  { q: "Après une rupture…", o: [
    { t: "Triste mais on rebondit sainement", s: "secure" },
    { t: "Difficile de lâcher, on rumine longtemps", s: "anxious" },
    { t: "On tourne la page vite (en apparence)", s: "avoidant" },
    { t: "Montagnes russes : manque intense puis rejet", s: "disorganized" },
  ]},
  { q: "La confiance envers l'autre…", o: [
    { t: "Accordée facilement, retirée si trahie", s: "secure" },
    { t: "Fragile, besoin de preuves régulières", s: "anxious" },
    { t: "Difficile, on préfère compter sur soi", s: "avoidant" },
    { t: "Instable : on doute puis on s'accroche", s: "disorganized" },
  ]},
];

export default function Quiz() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState("intro"); // intro | quiz | result
  const [mode, setMode] = useState("me");
  const [partnerName, setPartnerName] = useState("");
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [refCode, setRefCode] = useState(null);

  useEffect(() => {
    if (user) api.get("/referral/me").then((r) => setRefCode(r.data?.code)).catch(() => {});
  }, [user]);

  const result = useMemo(() => {
    if (answers.length < QUESTIONS.length) return null;
    const tally = { secure: 0, anxious: 0, avoidant: 0, disorganized: 0 };
    answers.forEach((s) => { tally[s] = (tally[s] || 0) + 1; });
    const top = Object.entries(tally).sort((a, b) => b[1] - a[1])[0];
    const pct = Math.round((top[1] / QUESTIONS.length) * 100);
    return { key: top[0], pct, tally };
  }, [answers]);

  const start = () => { vibrate(); setStep("quiz"); setIdx(0); setAnswers([]); };
  const pick = (s) => {
    vibrate();
    const next = [...answers.slice(0, idx), s];
    setAnswers(next);
    if (idx + 1 >= QUESTIONS.length) setStep("result");
    else setIdx(idx + 1);
  };
  const back = () => { if (idx > 0) setIdx(idx - 1); };
  const restart = () => { setStep("intro"); setIdx(0); setAnswers([]); };

  const subjectLabel = mode === "them" ? (partnerName.trim() || "ton/ta partenaire") : "toi";

  return (
    <div className="min-h-screen bg-[#09090b] text-white">
      <div className="max-w-md mx-auto px-5 py-6">
        <button data-testid="quiz-exit" onClick={() => navigate(user ? "/app" : "/")}
          className="flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white mb-4">
          <ArrowLeft size={16} /> {user ? "Retour à l'app" : "Accueil"}
        </button>

        {step === "intro" && (
          <QuizIntro mode={mode} setMode={setMode} partnerName={partnerName} setPartnerName={setPartnerName} onStart={start} />
        )}
        {step === "quiz" && (
          <QuizQuestions idx={idx} onPick={pick} onBack={back} subjectLabel={subjectLabel} mode={mode} />
        )}
        {step === "result" && result && (
          <QuizResult result={result} mode={mode} subjectLabel={subjectLabel} refCode={refCode}
            user={user} onRestart={restart} onSignup={() => navigate("/onboarding")} />
        )}
      </div>
    </div>
  );
}

function QuizIntro({ mode, setMode, partnerName, setPartnerName, onStart }) {
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="text-center" data-testid="quiz-intro">
      <div className="text-5xl mb-3">🧠💘</div>
      <h1 className="font-display text-3xl font-black leading-tight">Quel est ton style d'attachement ?</h1>
      <p className="text-sm text-zinc-400 mt-2">10 questions · 2 minutes · résultat partageable. Découvre comment tu (ou l'autre) fonctionne en amour.</p>
      <div className="mt-3 flex justify-center"><SocialProof /></div>

      <div className="mt-6 space-y-2 text-left">
        <p className="text-xs uppercase tracking-wider text-zinc-500 font-bold">Je fais le test sur…</p>
        <div className="grid grid-cols-2 gap-2">
          {[{ k: "me", label: "Moi", icon: Sparkles }, { k: "them", label: "Mon/ma partenaire", icon: Heart }].map(({ k, label, icon: Icon }) => (
            <button key={k} data-testid={`quiz-mode-${k}`} onClick={() => { vibrate(); setMode(k); }}
              className={`flex items-center justify-center gap-2 rounded-xl py-3 text-sm font-semibold border transition-colors ${
                mode === k ? "bg-pink-500/15 border-pink-500/40 text-pink-200" : "bg-zinc-900 border-zinc-800 text-zinc-400"}`}>
              <Icon size={15} /> {label}
            </button>
          ))}
        </div>
        {mode === "them" && (
          <input data-testid="quiz-partner-name" value={partnerName} onChange={(e) => setPartnerName(e.target.value)}
            placeholder="Son prénom (optionnel)" maxLength={20}
            className="w-full mt-1 rounded-xl bg-zinc-900 border border-zinc-800 px-4 py-3 text-sm outline-none focus:border-pink-500" />
        )}
      </div>

      <button data-testid="quiz-start" onClick={onStart}
        className="mt-6 w-full flex items-center justify-center gap-2 rounded-full bg-pink-500 hover:bg-pink-600 text-white font-bold py-4 active:scale-95 transition-transform">
        Commencer le test <ArrowRight size={18} />
      </button>
      <p className="text-[11px] text-zinc-600 mt-3">Test ludique inspiré de la théorie de l'attachement. Pas un diagnostic clinique.</p>
    </motion.div>
  );
}

function QuizQuestions({ idx, onPick, onBack, subjectLabel, mode }) {
  const question = QUESTIONS[idx];
  const progress = Math.round((idx / QUESTIONS.length) * 100);
  return (
    <div data-testid="quiz-questions">
      <div className="flex items-center gap-3 mb-5">
        {idx > 0 && <button data-testid="quiz-back" onClick={onBack} className="text-zinc-400 hover:text-white"><ArrowLeft size={18} /></button>}
        <div className="flex-1 h-2 rounded-full bg-zinc-800 overflow-hidden">
          <motion.div className="h-full bg-pink-500 rounded-full" animate={{ width: `${progress}%` }} />
        </div>
        <span className="text-xs text-zinc-500 font-semibold">{idx + 1}/{QUESTIONS.length}</span>
      </div>

      {mode === "them" && (
        <p className="text-[11px] text-pink-300/80 mb-2 text-center">Réponds selon ce que tu observes de {subjectLabel} 👀</p>
      )}

      <AnimatePresence mode="wait">
        <motion.div key={idx} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.2 }}>
          <h2 className="font-display text-xl font-black leading-snug mb-4">{question.q}</h2>
          <div className="space-y-2.5">
            {question.o.map((opt, i) => (
              <button key={i} data-testid={`quiz-option-${i}`} onClick={() => onPick(opt.s)}
                className="w-full text-left rounded-2xl bg-zinc-900 border border-zinc-800 px-4 py-4 text-sm font-medium hover:border-pink-500/50 hover:bg-zinc-800/60 active:scale-[0.98] transition-all">
                {opt.t}
              </button>
            ))}
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

function QuizResult({ result, mode, subjectLabel, refCode, user, onRestart, onSignup }) {
  const s = STYLES[result.key];
  const cardRef = useRef(null);
  const [sharing, setSharing] = useState(false);
  const [preview, setPreview] = useState(false);
  const [email, setEmail] = useState(user?.email || "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [linked, setLinked] = useState(false);
  const linkedOnce = useRef(false);

  // Logged-in "me" mode → silently attach the result to the account so it
  // personalises future conversation analyses (quiz↔analysis combo).
  useEffect(() => {
    if (user && mode === "me" && !linkedOnce.current) {
      linkedOnce.current = true;
      api.post("/quiz/save-me", { style: result.key, scores: result.tally })
        .then(() => setLinked(true)).catch(() => {});
    }
  }, [user, mode, result]);

  const shareUrl = refCode ? `${APP_URL}/quiz?ref=${refCode}` : `${APP_URL}/quiz`;
  const shareLabel = refCode ? `${APP_DOMAIN}/quiz` : `${APP_DOMAIN}/quiz`;
  const heading = mode === "them" ? `Le style de ${subjectLabel}` : "Ton style d'attachement";

  const save = async (e) => {
    e?.preventDefault();
    const em = email.trim().toLowerCase();
    if (!em.includes("@")) { toast.error("Entre un email valide"); return; }
    setSaving(true); vibrate();
    try {
      await api.post("/quiz/save", { email: em, style: result.key, mode, scores: result.tally, partner_name: mode === "them" ? subjectLabel : null });
      setSaved(true);
      toast.success("Résultat envoyé par email 📬");
    } catch { toast.error("Une erreur est survenue"); }
    finally { setSaving(false); }
  };

  const shareStory = async () => {
    if (!cardRef.current) return;
    setSharing(true); vibrate();
    try {
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 3, cacheBust: true, skipFonts: true, backgroundColor: s.g[0] });
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], "doclove-attachement.png", { type: "image/png" });
      const text = `Mon style d'attachement selon DocLove ${s.emoji} Fais le test : ${shareUrl}`;
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "Mon style d'attachement", text });
      } else {
        const a = document.createElement("a"); a.href = dataUrl; a.download = "doclove-attachement.png"; a.click();
        toast.success("Image enregistrée — partage-la en story ! 🔥");
      }
      setPreview(false);
    } catch (e) { if (e?.name !== "AbortError") toast.error("Partage impossible"); }
    finally { setSharing(false); }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-4" data-testid="quiz-result">
      <div className="rounded-3xl border p-6 text-center" style={{ borderColor: `${s.color}55`, background: `radial-gradient(120% 90% at 50% 0%, ${s.color}22 0%, rgba(24,24,27,.6) 55%, #18181b 100%)` }}>
        <div className="text-6xl mb-2">{s.emoji}</div>
        <p className="text-[11px] uppercase tracking-[0.2em] font-bold" style={{ color: s.accent }}>{heading}</p>
        <h1 className="font-display text-3xl font-black mt-1" data-testid="quiz-result-title">Attachement {s.title}</h1>
        <p className="text-sm font-semibold mt-1" style={{ color: s.accent }}>{result.pct}% · {s.short}</p>
        <p className="text-sm text-zinc-300 mt-3">{s.desc}</p>
      </div>

      <button data-testid="quiz-share" onClick={() => { vibrate(); setPreview(true); }}
        className="w-full flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-pink-500 to-purple-500 hover:opacity-90 text-white font-bold py-3.5 active:scale-95 transition-transform">
        <Share2 size={18} /> Partager mon résultat en story
      </button>

      <div className="rounded-2xl bg-zinc-900 border border-zinc-800 p-4">
        <p className="text-sm font-bold mb-1">💞 En couple</p>
        <p className="text-sm text-zinc-300">{s.couple}</p>
      </div>
      <div className="rounded-2xl bg-zinc-900 border border-zinc-800 p-4">
        <p className="text-sm font-bold mb-2">✅ Conseils du Doc</p>
        <ul className="space-y-2">
          {s.tips.map((t, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-zinc-300"><Sparkles size={14} className="text-pink-400 shrink-0 mt-0.5" /> {t}</li>
          ))}
        </ul>
      </div>

      {/* Email capture / signup / account-linked */}
      {linked ? (
        <div className="rounded-2xl bg-emerald-500/10 border border-emerald-500/25 p-4 flex items-start gap-3" data-testid="quiz-linked">
          <CheckCircle2 className="text-emerald-400 shrink-0 mt-0.5" size={20} />
          <div>
            <p className="text-sm font-semibold text-emerald-200">Enregistré sur ton compte ✓</p>
            <p className="text-xs text-zinc-400 mt-0.5">On l'utilisera pour personnaliser tes analyses de conversation (dynamique de couple, conseils sur mesure).</p>
          </div>
        </div>
      ) : saved ? (
        <div className="rounded-2xl bg-emerald-500/10 border border-emerald-500/25 p-5 text-center" data-testid="quiz-saved">
          <CheckCircle2 className="mx-auto text-emerald-400 mb-2" size={24} />
          <p className="text-sm font-semibold text-emerald-200">Résultat envoyé 📬</p>
          <p className="text-xs text-zinc-400 mt-1">Passe à la vitesse supérieure : analyse une vraie conversation pour découvrir le style de l'autre, les red flags et votre compatibilité.</p>
          <button data-testid="quiz-to-app" onClick={onSignup} className="mt-4 rounded-full bg-pink-500 hover:bg-pink-600 text-white text-sm font-bold px-6 py-3">
            Analyser une conversation →
          </button>
        </div>
      ) : (
        <form onSubmit={save} className="rounded-2xl bg-gradient-to-br from-pink-500/10 to-purple-500/10 border border-pink-500/25 p-5" data-testid="quiz-capture">
          <p className="text-sm font-bold">📩 Sauvegarde ton résultat</p>
          <p className="text-xs text-zinc-400 mt-0.5 mb-3">Reçois ton profil par email + comment mieux communiquer selon ton style.</p>
          <div className="flex items-center gap-2 rounded-full bg-zinc-950 border border-zinc-800 px-4 focus-within:border-pink-500/50">
            <Mail size={16} className="text-zinc-500 shrink-0" />
            <input data-testid="quiz-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="ton@email.com" className="flex-1 bg-transparent py-3 text-sm outline-none" />
          </div>
          <button data-testid="quiz-save" disabled={saving}
            className="mt-2 w-full flex items-center justify-center gap-2 rounded-full bg-pink-500 hover:bg-pink-600 text-white text-sm font-bold py-3 disabled:opacity-60">
            {saving ? <Loader2 size={16} className="animate-spin" /> : "Recevoir mon résultat"}
          </button>
        </form>
      )}

      <button data-testid="quiz-restart" onClick={onRestart}
        className="w-full flex items-center justify-center gap-2 rounded-2xl bg-zinc-900 border border-zinc-800 hover:border-pink-500/40 text-sm font-semibold py-3">
        <RotateCcw size={15} /> Refaire le test
      </button>

      {/* Shareable story card */}
      <AnimatePresence>
        {preview && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/85 backdrop-blur-sm p-4"
            onClick={() => setPreview(false)} data-testid="quiz-share-preview">
            <button data-testid="quiz-share-close" onClick={() => setPreview(false)}
              className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/10 text-white flex items-center justify-center"><X size={18} /></button>
            <motion.div initial={{ y: 24, scale: 0.96 }} animate={{ y: 0, scale: 1 }} onClick={(e) => e.stopPropagation()}>
              <div style={{ width: 288, height: 512, overflow: "hidden" }} className="rounded-3xl shadow-2xl mx-auto">
                <div style={{ transform: "scale(0.8)", transformOrigin: "top left", width: 360, height: 640 }}>
                  <div ref={cardRef} style={{ width: 360, height: 640 }} className="relative overflow-hidden flex flex-col p-7" data-testid="quiz-share-card">
                    <div className="absolute inset-0" style={{ background: `linear-gradient(160deg,${s.g[0]} 0%,${s.g[1]} 55%,${s.g[2]} 100%)` }} />
                    <div className="absolute w-56 h-56 rounded-full" style={{ top: -64, right: -64, background: `${s.color}66`, filter: "blur(60px)" }} />
                    <div className="absolute w-56 h-56 rounded-full" style={{ bottom: -80, left: -64, background: `${s.color}44`, filter: "blur(60px)" }} />

                    <div className="relative flex items-center gap-2 text-white">
                      <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center"><Brain size={18} /></div>
                      <span className="font-display text-lg font-black tracking-tight">DocLove</span>
                    </div>

                    <div className="relative flex-1 flex flex-col items-center justify-center text-center">
                      <div style={{ fontSize: 88, lineHeight: 1 }}>{s.emoji}</div>
                      <p className="text-[11px] font-bold tracking-[0.2em] mt-4" style={{ color: s.accent }}>{(heading || "").toUpperCase()}</p>
                      <p className="text-white font-display font-black leading-tight mt-1" style={{ fontSize: 34 }}>Attachement {s.title}</p>
                      <p className="font-black mt-2" style={{ fontSize: 20, color: s.accent }}>{result.pct}%</p>
                      <p className="text-white/85 text-sm mt-2 px-2">{s.short}</p>
                    </div>

                    <div className="relative text-center">
                      <p className="text-white/90 text-sm font-semibold">Et toi, c'est quoi ton style ?</p>
                      <p className="font-black text-base mt-1" style={{ color: s.accent }}>{shareLabel}</p>
                      {refCode && <p className="text-white/70 text-[11px] mt-0.5">Code {refCode} · 1 mois offert 🎁</p>}
                    </div>
                  </div>
                </div>
              </div>
              <button data-testid="quiz-share-confirm" onClick={shareStory} disabled={sharing}
                className="w-72 mx-auto mt-4 flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-pink-500 to-purple-500 text-white text-sm font-bold py-3 disabled:opacity-60">
                {sharing ? <Loader2 size={16} className="animate-spin" /> : <Share2 size={16} />}
                {sharing ? "Préparation…" : "Partager / Enregistrer"}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
